/**
 * Needs your attention → After the crew's end of day (1.5.0): a booking awaiting the Director's approval. Everyone
 * on the schedule sees it; only the administrator gets Approve (an optional note) and Decline (a reason that goes
 * back to the scheduler). The server refuses anyone else (403).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { AttentionItem } from '@shared/api-types';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatDayShort } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';

export function ApprovalItem({ item, isAdmin, dot }: { item: AttentionItem; isAdmin: boolean; dot: string }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'none' | 'approve' | 'decline'>('none');
  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = [item.unit, item.tenantName].filter(Boolean).join(' · ');
  const id = `appr-${item.id}`;

  const send = async (what: 'approve' | 'decline') => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/approvals/${encodeURIComponent(item.txId ?? item.id)}/${what}`, { method: 'POST', body: JSON.stringify(what === 'approve' ? { note: words.trim() || null } : { reason: words.trim() }) });
      const msg = what === 'approve' ? t('approval.approved', { who }) : t('approval.declined', { who });
      announce(msg);
      setMode('none');
      setWords('');
      void qc.invalidateQueries();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : String(e);
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`attn-approval-${item.id}`}>
      <span className="dot" style={{ background: dot }} aria-hidden="true" />
      <div className="t">
        <b>
          {who} · {t('approval.title')}
        </b>
        <span>
          {t('approval.sub', { day: formatDayShort(item.dayKey ?? '', i18n.language), span: item.span ?? '', by: item.by ?? '' })}
          {item.inProgress ? ` · ${t('attention.bookingPending')}` : ''}
        </span>
        {!isAdmin ? <span className="help">{t('approval.onlyDirector')}</span> : null}
        {mode !== 'none' ? (
          <div className="field" style={{ marginTop: 8 }}>
            <label htmlFor={id}>{mode === 'approve' ? t('approval.noteLabel') : t('approval.reasonLabel')}</label>
            <textarea id={id} className="input" value={words} onChange={(e) => setWords(e.target.value)} aria-required={mode === 'decline' || undefined} data-testid={`approval-words-${item.id}`} />
            <div className="row" style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" disabled={busy || (mode === 'decline' && !words.trim())} onClick={() => send(mode)} data-testid={`approval-send-${item.id}`}>
                {mode === 'approve' ? t('approval.approve') : t('approval.decline')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setMode('none')}>
                {t('book.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
        {error ? (
          <span className="field-err" role="alert">
            {error}
          </span>
        ) : null}
      </div>
      {isAdmin && mode === 'none' && !item.inProgress ? (
        <>
          <Button size="sm" aria-label={`${t('approval.approve')} · ${who}`} onClick={() => setMode('approve')} data-testid={`approval-approve-${item.id}`}>
            {t('approval.approve')}
          </Button>
          <Button size="sm" variant="secondary" aria-label={`${t('approval.decline')} · ${who}`} onClick={() => setMode('decline')} data-testid={`approval-decline-${item.id}`}>
            {t('approval.decline')}
          </Button>
        </>
      ) : null}
    </li>
  );
}

